import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search } from 'lucide-react';
import { useOpensUp } from '../../../components/common/useOpensUp';
import { Avatar, Spinner } from '../../../components/ui';
import { searchService } from '../../../services/searchService';
import { SearchEntityType } from '../../../types/search';
import { useDebounce } from '../../../hooks/useDebounce';
import type { SubjectRef } from '../../../types/monitoring';

export interface SubjectPickerProps {
  onPick: (subject: SubjectRef) => void;
  /** Ids already watched — shown as "Watched" and not selectable */
  exclude?: string[];
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  ariaLabel?: string;
}

/** One searchable picker over both stakeholders and organizations */
export const SubjectPicker: React.FC<SubjectPickerProps> = ({
  onPick,
  exclude = [],
  placeholder = 'Search people and organizations',
  autoFocus,
  className = '',
  ariaLabel,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Array<SubjectRef & { line: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const listId = useId();
  const debounced = useDebounce(query.trim(), 250);
  const excluded = useMemo(() => new Set(exclude), [exclude]);
  const blurTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(blurTimer.current), []);

  useEffect(() => {
    if (!debounced) {
      setResults([]);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    searchService
      .search({
        q: debounced,
        entityTypes: [SearchEntityType.STAKEHOLDER, SearchEntityType.ORGANIZATION],
        limit: 8,
      })
      .then((page) => {
        if (cancelled) return;
        setFailed(false);
        setResults(
          (page.items || []).map((item) => ({
            type: item.entityType === SearchEntityType.ORGANIZATION ? 'organization' : 'stakeholder',
            id: item.id,
            label: searchService.formatResultTitle(item.data, item.entityType),
            line: searchService.formatResultSubtitle(item.data, item.entityType),
          })),
        );
        setActive(0);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  const selectable = results.filter((result) => !excluded.has(result.id));

  const pick = (subject: SubjectRef) => {
    onPick({ type: subject.type, id: subject.id, label: subject.label });
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const opensUp = useOpensUp(open && !!query.trim(), fieldRef, 300);

  return (
    <div ref={fieldRef} className={`relative ${className}`}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" strokeWidth={1.75} />
      <input
        type="search"
        role="combobox"
        aria-expanded={open && !!query.trim()}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-label={ariaLabel || placeholder}
        autoFocus={autoFocus}
        value={query}
        placeholder={placeholder}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          blurTimer.current = window.setTimeout(() => setOpen(false), 140);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((index) => Math.min(selectable.length - 1, index + 1));
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((index) => Math.max(0, index - 1));
          } else if (event.key === 'Enter' && selectable[active]) {
            event.preventDefault();
            pick(selectable[active]);
          } else if (event.key === 'Escape') {
            setQuery('');
            setOpen(false);
          }
        }}
        className="block h-10 w-full rounded-full border-0 bg-neutral-100 pl-9 pr-9 text-[16px] text-ink placeholder:text-neutral-400 transition-shadow focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-600/25 sm:h-9 sm:text-[13px]"
      />
      {loading && <Spinner className="absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" />}

      {open && !!query.trim() && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Search results"
          className={`absolute left-0 right-0 z-40 ${opensUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5'} max-h-[300px] animate-fade-up overflow-y-auto overscroll-contain rounded-2xl bg-white p-1 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.22)] ring-1 ring-black/[0.06]`}
        >
          {failed ? (
            <li className="px-3 py-2.5 text-[13px] text-primary-700">Search is unavailable right now</li>
          ) : results.length === 0 ? (
            <li className="px-3 py-2.5 text-[13px] text-neutral-400">{loading ? 'Searching' : 'No matches'}</li>
          ) : (
            results.map((result) => {
              const already = excluded.has(result.id);
              const index = selectable.indexOf(result);
              return (
                <li key={`${result.type}-${result.id}`} role="option" aria-selected={!already && index === active}>
                  <button
                    type="button"
                    disabled={already}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => pick(result)}
                    onMouseEnter={() => index >= 0 && setActive(index)}
                    className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-left transition-colors ${
                      already ? 'cursor-default opacity-50' : index === active ? 'bg-neutral-100' : ''
                    }`}
                  >
                    <Avatar name={result.label} kind={result.type} size="xs" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] text-ink">{result.label}</span>
                      <span className="block truncate text-[11.5px] text-neutral-500">{result.line}</span>
                    </span>
                    {already && <span className="flex-shrink-0 text-[11.5px] text-neutral-400">Watched</span>}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};
